import { Link } from "react-router-dom";
import { ArrowRight, FlaskConical, ShieldCheck, Droplets } from "lucide-react";
import PageHero from "../components/PageHero";
import CTASection from "../components/CTASection";
import { Button } from "../components/ui/button";
import { DRUG_TESTING, BACKGROUND_SERVICES, LAB_SERVICES } from "../data/content";

const SECTIONS = [
  {
    id: "drug-testing",
    title: "Drug Testing",
    icon: FlaskConical,
    color: "teal",
    items: DRUG_TESTING,
    note: "DOT testing is performed when properly qualified. Laboratory analysis is conducted by certified laboratories.",
  },
  {
    id: "background",
    title: "Background Services",
    icon: ShieldCheck,
    color: "sage",
    items: BACKGROUND_SERVICES,
    note: "Screening results are provided by authorized consumer reporting and verification sources.",
  },
  {
    id: "lab-collections",
    title: "Lab / Identity Collections",
    icon: Droplets,
    color: "champagne",
    items: LAB_SERVICES,
    note: "Blood testing is performed only through appropriately licensed personnel and providers.",
  },
];

export default function Screening() {
  return (
    <div data-testid="screening-page">
      <PageHero
        eyebrow="Screening & Verification"
        title="Testing, Background & Verification Services"
        lead="Employment, licensing, and immigration processes often require verified testing and screening. We provide professional, confidential collection and coordination services where available and properly authorized."
      />

      <section className="py-16 lg:py-24" data-testid="screening-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {SECTIONS.map((section) => {
            const Icon = section.icon;
            const bg = section.color === "teal" ? "bg-teal-light" : section.color === "sage" ? "bg-sage-light" : "bg-champagne-light";
            const fg = section.color === "teal" ? "text-teal" : section.color === "sage" ? "text-sage" : "text-champagne-dark";
            const dot = section.color === "teal" ? "bg-teal" : section.color === "sage" ? "bg-sage" : "bg-champagne-dark";
            return (
              <div key={section.id} className="grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid={`screening-${section.id}`}>
                <div className="lg:col-span-4">
                  <div className={`h-11 w-11 rounded-xl ${bg} flex items-center justify-center mb-5`}>
                    <Icon className={`h-5 w-5 ${fg}`} />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-navy tracking-tight">{section.title}</h2>
                  <p className="mt-4 text-sm text-mutedink italic leading-relaxed">{section.note}</p>
                </div>
                <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {section.items.map((item) => (
                    <div key={item} className="flex items-start gap-3 bg-surface border border-line rounded-lg p-4">
                      <span className={`mt-1.5 h-2 w-2 rounded-full ${dot} flex-shrink-0`} />
                      <span className="text-sm text-ink">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="pb-16 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#101315] border border-teal/25 rounded-2xl p-8 lg:p-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <h2 className="text-2xl font-serif font-semibold text-cream">Need a test or screening appointment?</h2>
              <p className="mt-2 text-sm text-cream/70">Walk in or schedule ahead — we will confirm availability and what to bring.</p>
            </div>
            <Button asChild size="lg" className="bg-champagne text-espresso hover:bg-champagne-dark hover:text-espresso rounded-full px-8" data-testid="book-test-button">
              <Link to="/start">Book a Test <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      <CTASection title="Employers and individuals welcome." text="Pre-employment, DOT, court-ordered, and personal screening coordinated professionally and confidentially." primaryLabel="Book a Test" />
    </div>
  );
}
